import { FileAddOutlined, UploadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Row, Upload, Button } from 'antd';

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRODUCT } from '../../../../constants/define/translation';

import {
  ProductCatalog,
  ProductCatalogUpdate,
  updateProductCatalogRepository,
} from '../../../../repository/mytm/shop-product/shopProductRepository';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { ButtonActionPage, SpinLoading } from '../../../atoms';
import { PreviewProductCatalog } from '../../../molecules/preview/common';
import { DragDropImage } from '../../../molecules/upload';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';

interface FormProductCatalogProps {
  record: ProductCatalog;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
export const FormProductCatalog = (props: FormProductCatalogProps) => {
  const { t } = useTranslation(PRODUCT);
  const { record, onRefresh, onCloseModal, setDataRecord } = props;
  const [form] = Form.useForm();
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState({});

  useEffect(() => {
    if (record) {
      setImageList(record?.brandLogo ? [{ dataURL: record?.brandLogo, isImageURL: true }] : []);
      setDataForm(defaultValue);
    }
  }, [record]);

  useEffect(() => {
    if (imageList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList]);

  const defaultValue = {
    title: record?.productName,
    link: record?.link,
    tag: record?.tag,
    image: record?.image,
  };

  const onChangeInput = useCallback(
    e => {
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
    },
    [form],
  );

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onFinish = useCallback(
    (formValue: any) => {
      setCreateLoading(true);
      const imgList = imageList.filter(img => !img.isImageURL);
      const imgLink = imageList.filter(img => img.isImageURL);
      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const imageUrl = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];

          const params: ProductCatalogUpdate = {
            id: record?.id,
            product_id: record.product_id,
            link: formValue?.link,
            brandLogo: imageUrl || '',
            tag: formValue?.tag,
            country: record?.country || 'USA',
          };
          return updateProductCatalogRepository(params)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.updateDiscountItemSuccess'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setCreateLoading(false);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList],
  );

  const action = {
    beforeUpload(file: any): Promise<any> {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const img = document.createElement('img');
          img.src = reader?.result ? reader?.result.toString() : '';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0);
            }
            const url = canvas.toDataURL();
            canvas.toBlob(
              blob => {
                const uploadedFiles = [
                  {
                    dataURL: url,
                    blob,
                    fileName: file.name,
                    fileType: file.type,
                    isImageURL: false,
                  },
                ];
                onSetImageList(uploadedFiles);
                onSelectedImage(uploadedFiles.length - 1);
                resolve(blob);
              },
              file.type || 'image/jpeg',
              1,
            );
          };
        };
      });
    },
  };

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-product">
            <Row className="form-product-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImage
                  {...{
                    imageList,
                    onSetImageList,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewProductCatalog
                        values={{
                          ...dataForm,
                          backgroundImage: record?.image,
                          logoPartner: imageList?.length && imageList[0].dataURL,
                          price: record?.price,
                          priceDiscount: record?.priceDiscount,
                        }}
                        height="330px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormPromotion span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input disabled placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="tag" label={t('form.tag')}>
                        <Input placeholder={t('form.tag')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="link"
                        label={t('form.link')}
                        rules={[{ required: true, message: t('validate.linkBlank') }]}
                      >
                        <Input placeholder={t('form.link')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="logo" initialValue={imageList.length} label={t('form.logo')}>
                        <Upload {...action} maxCount={1} accept="image/*">
                          <Button
                            style={{ background: '#000', color: '#fff', borderRadius: '5px' }}
                            icon={<UploadOutlined />}
                          >
                            {t('form.upload')}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={t('form.update')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormPromotion>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & .form-promotion-container {
    padding: 1.5rem;

    .form-promotion__description {
      resize: none;
    }
  }

  .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 30%;
  }

  .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .sketch-picker {
    cursor: pointer;
  }

  & .ant-slider:hover .ant-slider-rail,
  & .ant-slider-rail {
    background-color: #6e6e6e;
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormPromotion = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .ant-upload-span {
    display: none !important;
  }
`;
